import {
  LandingPage,
  LandingPageTopper,
} from "design-system/components/pages/landing-page/landing-page";
import { Footnotes } from "design-system/components/primitives/footnote/footnotes";
import { ProgramComparisonProvider } from "design-system/hooks/use-program-comparison";
import { ProgramComparisonTray } from "design-system/sites/exed/program-comparison-tray/program-comparison-tray";
import { createContentfulComponent } from "frontend/contentful/lib/create-contentful-component";
import { LandingPageEntry } from "frontend/contentful/schema/pages";
import { useContentful } from "frontend/hooks/use-contentful";
import { usePageFootnotes } from "frontend/hooks/use-page-footnotes";
import { usePageNavigation } from "frontend/hooks/use-page-navigation";
import { usePageProps } from "frontend/hooks/use-page-props";
import { checkNeedsProgramComparison } from "frontend/lib/needs-program-comparison";
import dynamic from "next/dynamic";
import { ContentfulHead } from "../primitives/head.contentful";
import { createLazyTopperProps } from "../toppers/lazy-topper.props";
import { createSiteFooterProps } from "../universal/site-footer.props";
import { createSiteHeaderProps } from "../universal/site-header.props";
import { ContentfulPageSections } from "./page-sections.contentful";
import { isContentType } from "frontend/contentful/lib/is-content-type";
import {
  ContentReferencesEntry,
  OnlineContentReferencesEntry,
} from "frontend/contentful/schema/universal";
import { getProgramStructuredData } from "../utils/get-program-structured-data";
import { EntryRef } from "frontend/contentful/schema/sys";
import { LinkEntry } from "frontend/contentful/schema/primitives";
import { getBreadcrumbStructuredData } from "../utils/get-breadcrumb-structured-data";

const ContentfulHomePage = dynamic(() =>
  import("./home-page.contentful").then((e) => ({
    default: e.ContentfulHomePage,
  })),
);

export const ContentfulLandingPage =
  createContentfulComponent<LandingPageEntry>(({ entry }) => {
    const { singletons, includes } = usePageProps();
    const { site, getEntry, getEntryUrl } = useContentful();
    const { breadcrumbs } = usePageNavigation();
    const footnotes = usePageFootnotes(entry.fields.blocks);

    if (singletons.isWWWHomePage) {
      return <ContentfulHomePage entry={entry} />;
    }

    const siteContentReferences = getEntry(site?.fields.contentReferences);
    const needsProgramComparison = checkNeedsProgramComparison(includes);
    let finderPageRef: EntryRef<LinkEntry> | undefined = undefined;
    if (
      isContentType<ContentReferencesEntry>(
        "contentReferences",
        siteContentReferences,
      )
    ) {
      finderPageRef = siteContentReferences?.fields.programFinderPage;
    } else if (
      isContentType<OnlineContentReferencesEntry>(
        "onlineContentReferences",
        siteContentReferences,
      )
    ) {
      finderPageRef = siteContentReferences?.fields.courseFinderPage;
    }

    let finderUrl: string | undefined;

    if (finderPageRef) {
      finderUrl = getEntryUrl(finderPageRef);
    }

    let educationalProgramData = undefined;
    if (
      site?.fields.title === "Online" &&
      entry.fields.url?.indexOf("subjects") !== -1
    ) {
      educationalProgramData = getProgramStructuredData(entry, site);
    }
    let breadcrumbStructuredData = undefined;
    if (site?.fields.domain === "exed" && breadcrumbs.breadcrumbs.length > 0) {
      breadcrumbStructuredData = getBreadcrumbStructuredData(breadcrumbs, site);
    }

    return (
      <>
        <ContentfulHead
          entry={entry.fields.seo}
          educationalProgramData={educationalProgramData}
          breadcrumbStructuredData={breadcrumbStructuredData}
        />

        <LandingPage
          breadcrumbs={breadcrumbs}
          topper={
            createLazyTopperProps(entry.fields.topper) as LandingPageTopper
          }
          header={{ name: "SiteHeader", props: createSiteHeaderProps(site) }}
          footer={createSiteFooterProps(site?.fields.footer)}
        >
          {needsProgramComparison ? (
            <ProgramComparisonProvider programs={[]}>
              <ContentfulPageSections entry={entry} />
              <Footnotes footnotes={footnotes} />
              <ProgramComparisonTray
                contentReferences={siteContentReferences}
                isHBSOnline={site?.fields.domain === "online"}
                programFinderUrl={finderUrl}
              />
            </ProgramComparisonProvider>
          ) : (
            <>
              <ContentfulPageSections entry={entry} />
              <Footnotes footnotes={footnotes} />
            </>
          )}
        </LandingPage>
      </>
    );
  });
